import { FC, useCallback, useState } from "react";

import { AssignedPagesList } from "components/materials/AssignedPagesList";
import { MoreItemsButton } from "components/materials/MoreItemsButton";
import { ReadyForAssessmentPagesList } from "components/materials/ReadyForAssessmentPagesList";
import { RecentlyAssessedPagesList } from "components/materials/RecentlyAssessedPagesList";
import { getMaxItemsPerSection } from "components/materials/helper";
import { CoreUserRoleType } from "components/server-types";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { HorizontalDivider } from "mds/components/HorizontalDivider";
import { useCapabilities } from "store/capability-hooks";

type PlannerSectionCountType = {
  due: number;
  late: number;
  assess: number;
};

type PlannerProps = {
  shouldLinkSidebar?: boolean;
  showAll?: boolean;
  role?: CoreUserRoleType;
};

export const Planner: FC<PlannerProps> = ({ shouldLinkSidebar, showAll: showAllAlways, role }) => {
  const [sectionCounts, setSectionCounts] = useState<PlannerSectionCountType>(
    {} as PlannerSectionCountType,
  );
  const [showAll, setShowAll] = useState<boolean>(!!showAllAlways);
  const { canAuthorSection } = useCapabilities();
  const hasRole = Boolean(role);
  const maxItems = getMaxItemsPerSection(hasRole);
  const moreThanMaxItems = Object.values(sectionCounts).some((count) => count > maxItems);

  const setSectionCount = useCallback((section: keyof PlannerSectionCountType, count: number) => {
    setSectionCounts((prev) => ({ ...prev, [section]: count }));
  }, []);

  const shouldShowLinkButton = (section: keyof PlannerSectionCountType) => {
    const count = sectionCounts[section];
    return count !== undefined && count > maxItems && !showAll && !showAllAlways;
  };

  const handleDueCountChanged = useCallback(
    (count: number) => setSectionCount("due", count),
    [setSectionCount],
  );

  const handleLateCountChanged = useCallback(
    (count: number) => setSectionCount("late", count),
    [setSectionCount],
  );

  const handleAssessCountChanged = useCallback(
    (count: number) => setSectionCount("assess", count),
    [setSectionCount],
  );

  return (
    <>
      <AssignedPagesList
        category="all"
        role={role}
        shouldLinkSidebar={shouldLinkSidebar}
        showAll={showAll}
        timeframe="due-soon"
        onCountChanged={handleDueCountChanged}
      />

      {shouldShowLinkButton("due") && (
        <MoreItemsButton
          count={sectionCounts.due}
          maxItems={maxItems}
          onClick={() => setShowAll(true)}
        />
      )}

      <HorizontalDivider className="mb-4 mt-6" />

      <AssignedPagesList
        category="all"
        role={role}
        shouldLinkSidebar={shouldLinkSidebar}
        showAll={showAll}
        timeframe="late-only"
        onCountChanged={handleLateCountChanged}
      />

      {shouldShowLinkButton("late") && (
        <MoreItemsButton
          count={sectionCounts.late}
          maxItems={maxItems}
          onClick={() => setShowAll(true)}
        />
      )}

      <HorizontalDivider className="mb-4 mt-6" />

      {role !== "student" && canAuthorSection ? (
        <ReadyForAssessmentPagesList
          role={role}
          shouldLinkSidebar={shouldLinkSidebar}
          showAll={showAll}
          onCountChanged={handleAssessCountChanged}
        />
      ) : (
        <RecentlyAssessedPagesList
          role={role}
          shouldLinkSidebar={shouldLinkSidebar}
          showAll={showAll}
          onCountChanged={handleAssessCountChanged}
        />
      )}

      {shouldShowLinkButton("assess") && (
        <MoreItemsButton
          count={sectionCounts.assess}
          maxItems={maxItems}
          onClick={() => setShowAll(true)}
        />
      )}

      {moreThanMaxItems && !showAllAlways && (
        <Button
          className="mb-3 mt-1"
          kind="white"
          size="s"
          onClick={() => setShowAll((prev) => !prev)}
        >
          {showAll ? t("common.view_less") : t("common.view_all")}
        </Button>
      )}
    </>
  );
};
